import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { showModalSuccess } from "@src/redux/modalTypeSuccess";
import { AppDispatch, RootState } from "@src/redux/store";
import { CurrenciesIDEnum } from "@src/views/Balances/Components/currencies.types";
import { DOCUMENT_TYPES_BY_ENTITY } from "@src/utility/documentTypes";
import { fileToBase64 } from "@src/utility/fileToBase64";
import { showModalError } from "@src/redux/modalTypeError";
import { getApiErrorMessage } from "@src/utility/requestErrors";

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_FRONT_URL || "http://localhost:8000"
});

export interface PublicContractorState {
  countries: any[];
  states: any[];
  cities: any[];
  sellers: any[];
  taxSystems: any[];
  saving: boolean;
}

const normalizeOptions = (payload: any) => {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return rows.map((item: any) => ({
    ...item,
    value: item?.value ?? item?.id ?? item?.seller_id ?? item?.tax_system_id,
    label:
      item?.label ??
      item?.tax_system ??
      item?.description ??
      item?.name ??
      item?.full_name ??
      item?.seller_name ??
      item?.email
  }));
};

export const getPublicCountries = createAsyncThunk(
  "externalContractor/getCountries",
  async () => {
    const response = await publicApi.get("api/get_countries?filter=all");
    return {
      data: response.data
    };
  }
);

export const getPublicStates = createAsyncThunk(
  "externalContractor/getStates",
  async (countryId: number | string) => {
    const response = await publicApi.get(`api/get_states/${countryId}`);
    return {
      data: response.data
    };
  }
);

export const getPublicCities = createAsyncThunk(
  "externalContractor/getCities",
  async (stateId: number | string) => {
    const response = await publicApi.get(`api/get_cities/${stateId}`);
    return {
      data: response.data
    };
  }
);

export const getPublicSellers = createAsyncThunk(
  "externalContractor/getSellers",
  async () => {
    const response = await publicApi.get("api/get_seller");
    return {
      data: normalizeOptions(response.data)
    };
  }
);

export const getPublicTaxSystems = createAsyncThunk(
  "externalContractor/getTaxSystems",
  async (type: "moral" | "physical") => {
    const response = await publicApi.get("api/get_tax_systems", {
      params: {
        format: "options",
        type
      }
    });
    return {
      data: normalizeOptions(response.data)
    };
  }
);

export const createExternalContractor = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>("externalContractor/create", async (payload, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  // Fiscal names come from "Información necesaria" (physical person); moral
  // persons don't capture names there, so fall back to the contact data.
  const isPhysical = payload.tipo_entidad !== "moral";
  const fiscalNames = isPhysical
    ? payload.nombres
    : payload.contacto_nombres;
  const fiscalLastName = isPhysical
    ? payload.apellido_paterno
    : payload.contacto_apellido_paterno;
  const fiscalLastName2 = isPhysical
    ? payload.apellido_materno
    : payload.contacto_apellido_materno;

  const mappedPayload = {
    token: payload.token,
    name_contractor: payload.nombre_contratista,
    status: true,
    giro: payload.giro,
    contrato: payload.contrato,
    email: payload.correo_electronico,
    user_name: payload.contacto_nombres,
    phone: payload.telefono,
    password: payload.password,
    password_confirmation: payload.password_confirmation,
    seller_id: payload.seller_id,
    provider_id: payload.provider_id,
    business_line_id: businessLineSelected?.id,
    product_id: productSelected?.value,
    procedencia: "mexican",
    billing_type: payload.tipo_entidad,
    razon_social: payload.razon_social,
    regimen_juridico: payload.tipo_entidad,
    nombres: fiscalNames,
    apellido_paterno: fiscalLastName,
    apellido_materno: fiscalLastName2,
    // Keys read by Facturapi to build legal_name
    primer_apellido: fiscalLastName,
    segundo_apellido: fiscalLastName2,
    contacto_nombres: payload.contacto_nombres,
    contacto_apellido_paterno: payload.contacto_apellido_paterno,
    contacto_apellido_materno: payload.contacto_apellido_materno,
    pais: payload.pais,
    estado: payload.estado,
    ciudad: payload.ciudad,
    localidad: payload.localidad,
    codigo_postal: payload.codigo_postal,
    colonia: payload.colonia || "NA",
    calle_numero: payload.calle_numero,
    tax_id: payload.tax_id,
    rfc: payload.rfc,
    uso_cfdi: 26,
    regimen_fiscal: payload.regimen_fiscal,
    product: productSelected?.value,
    business_line: businessLineSelected?.id,
    currency_id: CurrenciesIDEnum.MXN
  };

  const entityType = payload.tipo_entidad === "moral" ? "moral" : "physical";
  const documentsMap = DOCUMENT_TYPES_BY_ENTITY[entityType];
  const documentEntries = Object.entries(documentsMap);
  const documents = await Promise.all(
    documentEntries.map(async ([fieldName, documentType]) => {
      const file = payload[fieldName];
      if (!file || !documentType) return null;
      const base64 = await fileToBase64(file);
      return {
        document_type: documentType,
        original_name: file.name || fieldName,
        file: [base64]
      };
    })
  );

  try {
    const response = await publicApi.post("api/create_external_contractor", {
      ...mappedPayload,
      documents: documents.filter(Boolean)
    });

    if (response.data.status === "success") {
      await dispatch(
        showModalSuccess({
          message: "Utiliza tu correo y contraseña para acceder al portal",
          url: "/login"
        })
      );
    }

    return {
      data: response.data
    };
  } catch (error) {
    const message = getApiErrorMessage(error);
    dispatch(showModalError({ message }));
    return rejectWithValue(message);
  }
});

const initialState: PublicContractorState = {
  countries: [],
  states: [],
  cities: [],
  sellers: [],
  taxSystems: [],
  saving: false
};

export const externalContractorSlice = createSlice({
  name: "externalContractor",
  initialState,
  reducers: {
    resetExternalContractor: (state) => {
      state.countries = [];
      state.states = [];
      state.cities = [];
      state.sellers = [];
      state.taxSystems = [];
      state.saving = false;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getPublicCountries.fulfilled, (state, action) => {
      state.countries = action.payload.data;
    });
    builder.addCase(getPublicStates.fulfilled, (state, action) => {
      state.states = action.payload.data;
      state.cities = [];
    });
    builder.addCase(getPublicCities.fulfilled, (state, action) => {
      state.cities = action.payload.data;
    });
    builder.addCase(getPublicSellers.fulfilled, (state, action) => {
      state.sellers = action.payload.data;
    });
    builder.addCase(getPublicTaxSystems.fulfilled, (state, action) => {
      state.taxSystems = action.payload.data;
    });
    builder.addCase(createExternalContractor.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(createExternalContractor.fulfilled, (state) => {
      state.saving = false;
    });
    builder.addCase(createExternalContractor.rejected, (state) => {
      state.saving = false;
    });
  }
});

export const { resetExternalContractor } = externalContractorSlice.actions;

export default externalContractorSlice.reducer;
